import React, { useState, useRef, useEffect } from 'react';

const APPLE_THEME = {
  white: '#ffffff',
  black: '#1d1d1f',
  textPrimary: '#1d1d1f',
  textSecondary: '#86868b',
  lightGray: '#f5f5f7',
  backgroundGray: '#fbfbfd',
  borderLight: '#d2d2d7',
  appleBlue: '#0066cc',
  systemBlue: '#007aff',
  whatsappGreen: '#25D366',
  emerald: '#30d158',
  cardBackground: 'rgba(255, 255, 255, 0.8)',
  cardBorder: 'rgba(0, 0, 0, 0.04)'
};

const TYPOGRAPHY = {
  display: '-apple-system, BlinkMacSystemFont, "SF Pro Display", sans-serif',
  text: '-apple-system, BlinkMacSystemFont, "SF Pro Text", sans-serif'
};

const useIntersectionObserver = (threshold = 0.1, rootMargin = '0px') => {
  const [isVisible, setIsVisible] = useState(false);
  const ref = useRef();

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsVisible(entry.isIntersecting);
      },
      { threshold, rootMargin }
    );

    const currentRef = ref.current;
    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, [threshold, rootMargin]);

  return [ref, isVisible];
};

const CTA = () => {
  const [ref, isVisible] = useIntersectionObserver(0.1);
  const [hoveredButton, setHoveredButton] = useState(null);

  return (
    <section id="contact" style={{ padding: '56px 20px 80px 20px', background: APPLE_THEME.lightGray }}>
      <div 
        ref={ref}
        style={{ 
          maxWidth: '980px', 
          margin: '0 auto'
        }}
      >
        <div style={{ textAlign: 'center', marginBottom: 28, opacity: isVisible ? 1 : 0, transform: isVisible ? 'translateY(0)' : 'translateY(16px)', transition: 'all 0.5s ease-out' }}>
          <h1 style={{ color: APPLE_THEME.textPrimary, fontFamily: TYPOGRAPHY.display, fontSize: 'clamp(32px, 4.6vw, 48px)', fontWeight: 700, lineHeight: 1.1, letterSpacing: '-0.01em', marginBottom: 10 }}>
            Destek alın
          </h1>
          <p style={{ color: APPLE_THEME.textSecondary, fontFamily: TYPOGRAPHY.text, fontSize: '18px', maxWidth: 620, margin: '0 auto' }}>
            İhtiyacınız olduğunda hızlıca ulaşın.
          </p>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 16, marginBottom: 24, opacity: isVisible ? 1 : 0, transform: isVisible ? 'translateY(0)' : 'translateY(14px)', transition: 'all 0.5s ease-out 0.1s' }}>
          <div
            style={{
              background: APPLE_THEME.white,
              borderRadius: '20px',
              padding: '28px 24px',
              border: `1px solid ${APPLE_THEME.cardBorder}`,
              boxShadow: '0 2px 12px rgba(0, 0, 0, 0.04)',
              textAlign: 'center',
              transition: 'all 0.3s ease',
              cursor: 'pointer'
            }}
          >
            <div 
              style={{
                width: 52,
                height: 52,
                background: APPLE_THEME.systemBlue,
                borderRadius: 16,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 18px auto'
              }}
            >
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
                <path 
                  d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72 12.84 12.84 0 00.7 2.81 2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45 12.84 12.84 0 002.81.7A2 2 0 0122 16.92z" 
                  stroke="white" 
                  strokeWidth="2" 
                  strokeLinecap="round" 
                  strokeLinejoin="round"
                />
              </svg>
            </div>
            <h3 style={{ color: APPLE_THEME.textPrimary, fontFamily: TYPOGRAPHY.display, fontSize: 22, fontWeight: 600, marginBottom: 8 }}>
              Telefonla destek
            </h3>
            <p 
              style={{
                color: APPLE_THEME.textSecondary,
                fontFamily: TYPOGRAPHY.text,
                fontSize: 15,
                lineHeight: '1.5',
                marginBottom: 18
              }}
            >
              Bir uzmanla doğrudan konuşun
            </p>
            <a
              href="tel:+905382477373"
              onMouseEnter={() => setHoveredButton('phone')}
              onMouseLeave={() => setHoveredButton(null)}
              style={{
                display: 'inline-block',
                padding: '10px 16px',
                borderRadius: 8,
                color: APPLE_THEME.systemBlue,
                fontFamily: TYPOGRAPHY.text,
                fontSize: 16,
                fontWeight: '400',
                textDecoration: 'none',
                background: hoveredButton === 'phone' ? 'rgba(0, 122, 255, 0.1)' : 'transparent',
                border: `1px solid ${APPLE_THEME.systemBlue}`,
                transition: 'all 0.2s ease'
              }}
            >
              Hemen ara
            </a>
          </div>

          <div
            style={{
              background: APPLE_THEME.white,
              borderRadius: '20px',
              padding: '28px 24px',
              border: `1px solid ${APPLE_THEME.cardBorder}`,
              boxShadow: '0 2px 12px rgba(0, 0, 0, 0.04)',
              textAlign: 'center',
              transition: 'all 0.3s ease',
              cursor: 'pointer'
            }}
          >
            <div style={{ width: 52, height: 52, background: APPLE_THEME.whatsappGreen, borderRadius: 16, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 18px auto' }}>
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
                <path 
                  d="M21 11.5a8.38 8.38 0 01-.9 3.8 8.5 8.5 0 01-7.6 4.7 8.38 8.38 0 01-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 01-.9-3.8 8.5 8.5 0 014.7-7.6 8.38 8.38 0 013.8-.9h.5a8.48 8.48 0 018 8v.5z" 
                  stroke="white" 
                  strokeWidth="2" 
                  strokeLinecap="round" 
                  strokeLinejoin="round"
                />
              </svg>
            </div>
            <h3 style={{ color: APPLE_THEME.textPrimary, fontFamily: TYPOGRAPHY.display, fontSize: 22, fontWeight: 600, marginBottom: 8 }}>
              WhatsApp ile iletişim
            </h3>
            <p 
              style={{
                color: APPLE_THEME.textSecondary,
                fontFamily: TYPOGRAPHY.text,
                fontSize: 15,
                lineHeight: '1.5',
                marginBottom: 18
              }}
            >
              Hızlı mesajlaşma ile destek alın
            </p>
            <a
              href="https://wa.me/+905382477373"
              onMouseEnter={() => setHoveredButton('whatsapp')}
              onMouseLeave={() => setHoveredButton(null)}
              style={{
                display: 'inline-block',
                padding: '10px 16px',
                borderRadius: 8,
                color: APPLE_THEME.whatsappGreen,
                fontFamily: TYPOGRAPHY.text,
                fontSize: 16,
                fontWeight: '400',
                textDecoration: 'none',
                background: hoveredButton === 'whatsapp' ? 'rgba(37, 211, 102, 0.1)' : 'transparent',
                border: `1px solid ${APPLE_THEME.whatsappGreen}`,
                transition: 'all 0.2s ease'
              }}
            >
              Mesaj gönder
            </a>
          </div>
        </div>

        <div style={{ opacity: isVisible ? 1 : 0, transform: isVisible ? 'translateY(0)' : 'translateY(12px)', transition: 'all 0.5s ease-out 0.2s' }}>
          <div style={{ background: APPLE_THEME.white, borderRadius: 20, padding: 28, border: `1px solid ${APPLE_THEME.cardBorder}`, boxShadow: '0 2px 12px rgba(0,0,0,0.04)', maxWidth: 580, margin: '0 auto' }}>
            <h2 style={{ color: APPLE_THEME.textPrimary, fontFamily: TYPOGRAPHY.display, fontSize: 22, fontWeight: 600, textAlign: 'center', marginBottom: 18 }}>Çalışma saatleri</h2>
            <div style={{ display: 'grid', gap: 1 }}>
              {[
                { day: 'Pazartesi–Cuma', hours: '09:00–19:00', active: true },
                { day: 'Cumartesi', hours: '10:00–18:00', active: true },
                { day: 'Pazar', hours: 'Kapalı', active: false }
              ].map((item, index) => (
                <div key={index} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 18px', background: index === 0 ? APPLE_THEME.lightGray : APPLE_THEME.white, borderTop: index === 0 ? 'none' : `1px solid ${APPLE_THEME.borderLight}`, borderRadius: index === 0 ? '12px 12px 0 0' : index === 2 ? '0 0 12px 12px' : 0 }}>
                  <span style={{ color: APPLE_THEME.textPrimary, fontFamily: TYPOGRAPHY.text, fontSize: 16 }}>{item.day}</span>
                  <span style={{ color: item.active ? APPLE_THEME.textPrimary : APPLE_THEME.textSecondary, fontFamily: TYPOGRAPHY.text, fontSize: 16 }}>{item.hours}</span>
                </div>
              ))}
            </div>
            <div style={{ marginTop: 18, paddingTop: 18, borderTop: `1px solid ${APPLE_THEME.borderLight}`, textAlign: 'center' }}>
              <p style={{ color: APPLE_THEME.textSecondary, fontFamily: TYPOGRAPHY.text, fontSize: 14, marginBottom: 6 }}>Acil durumlar için</p>
              <a href="tel:+905382477373" style={{ color: APPLE_THEME.systemBlue, fontFamily: TYPOGRAPHY.text, fontSize: 18, textDecoration: 'none' }}>+90 538 247 73 73</a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CTA;